import "./index.css";
import Map, { Layer, MapRef, Marker, Source } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";

import AirportMarker from "./AirportMarker/AirportMarker";
import FlightMarker from "./FlightMarker/FlightMarker";

import { Airport } from "../../models/airport";
import { Flight } from "../../models/flight";
import { LegacyRef, useEffect, useMemo, useRef } from "react";
import { getDistance, stateForDistance } from "@/utils/airport_distance";
import { Guess } from "@/reducers/RoundReducer";

interface GameMapProps {
  centerLat: number;
  centerLng: number;
  airports: {
    airport: Airport;
    state: "default" | "cold" | "warm" | "hot" | "origin" | "destination";
    distance?: number;
  }[];
  currentFlight: Flight | null;
  path: [lat: number, lon: number][] | null;
  latestGuess: Guess | null;
  showAirports: boolean;
  theme: "light" | "dark";
}

function GameMap({
  centerLat,
  centerLng,
  airports,
  currentFlight,
  path,
  latestGuess,
  showAirports,
  theme,
}: GameMapProps) {
  const mapRef = useRef<MapRef>();

  const navigateToPosition = (lat: number, lon: number, zoom: number = 4) => {
    const isMediumOrSmaller = window.innerWidth < 992; // Assuming 992px is the breakpoint for large screens
    if (isMediumOrSmaller) {
      lat -= zoom === 4 ? 6 : 1.5;
    } else {
      lat -= zoom === 4 ? 3 : 0;
    }

    mapRef.current?.flyTo({
      center: [lon, lat],
      zoom: zoom,
      duration: 2000,
    });
  };

  useEffect(() => {
    setTimeout(() => {
      let lat = currentFlight?.position.lat;
      let lon = currentFlight?.position.lon;

      if (lat && lon) {
        navigateToPosition(lat, lon);
      }
    }, 500);
  }, [
    currentFlight?.position.lat,
    currentFlight?.position.lon,
    mapRef.current,
  ]);

  useEffect(() => {
    if (!latestGuess) {
      return;
    }

    if (latestGuess.isCorrect) {
      navigateToPosition(
        currentFlight?.position.lat || 0,
        currentFlight?.position.lon || 0,
        4
      );
    }

    const actualAirport =
      latestGuess.type === "origin"
        ? currentFlight?.origin_airport
        : currentFlight?.destination_airport;

    const distance = actualAirport
      ? getDistance(latestGuess.airport, actualAirport)
      : null;

    if (distance && ["warm", "hot"].includes(stateForDistance(distance))) {
      navigateToPosition(
        latestGuess.airport.lat,
        latestGuess.airport.lon,
        stateForDistance(distance) === "hot" ? 6 : 4
      );
    }
  }, [latestGuess]);

  const airportMarkers = useMemo(() => {
    return airports
      .map(({ airport, state, distance }) => {
        if (showAirports || state !== "default") {
          return (
            <Marker
              key={airport.id}
              longitude={airport.lon}
              latitude={airport.lat}
            >
              <AirportMarker
                airport={airport}
                state={state || "default"}
                distance={distance}
              />
            </Marker>
          );
        }

        return null;
      })
      .filter((marker) => marker !== null);
  }, [airports]);

  return (
    <>
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          width: "100%",
          height: "100%",
        }}
      >
        <Map
          ref={mapRef as LegacyRef<MapRef>}
          mapboxAccessToken="pk.eyJ1Ijoia2lyYW5wYW5lc2FyIiwiYSI6ImNreHJyNTdlazBrb2QycG8wcGd4bmIxNmkifQ.WC9rlsZlW039F2ydh32vSw"
          initialViewState={{
            longitude: centerLng,
            latitude: centerLat,
            zoom: 2,
          }}
          mapStyle={
            theme === "light"
              ? "mapbox://styles/mapbox/streets-v12"
              : "mapbox://styles/mapbox/dark-v11"
          }
          projection={{ name: "globe" }}
        >
          {airportMarkers}

          {currentFlight && (
            <Marker
              key={currentFlight.id}
              longitude={currentFlight.position.lon}
              latitude={currentFlight.position.lat}
            >
              <FlightMarker
                icon={currentFlight.aircraft_icon}
                direction={currentFlight.position.direction}
                planeModel={currentFlight.aircraft_name}
              />
            </Marker>
          )}
          <Source
            id="flight-path"
            type="geojson"
            data={{
              type: "Feature",
              properties: {},
              geometry: { type: "LineString", coordinates: path },
            }}
          >
            <Layer
              id="flight-path"
              type="line"
              paint={{ "line-color": "#b5862b", "line-width": 4 }}
            />
          </Source>
        </Map>
      </div>
    </>
  );
}

export default GameMap;
